import { BaseTexture, BLEND_MODES, Container, IDestroyOptions, Rectangle, SCALE_MODES, Sprite, Texture } from "pixi.js"
import { TileSize } from "game-common/models"
import { GroundTone, groundToneCell } from "game-common/room/ground_tone"

/**
 * The ground's tone (game-common room/ground_tone.ts): grass that is damp darkens toward a cool moss green, grass that
 * is sun-dried warms toward straw. Two sprites over map1, one texel per cell, linearly filtered so the colour eases
 * from cell centre to cell centre: a MULTIPLY (damp, and the blue the sun bleaches out) then an ADD (the sun's warmth,
 * and the cool the damp lifts into its shadows).
 * Everything baked from the ground's pixels (raised banks, water banks, hill bands) reads it through toneGroundPixels,
 * with the same filtering, so a bake meets the flat ground without a seam.
 *
 * Live tuning: window.groundTone.enabled / .damp / .dampAdd / .sunMul / .sunAdd, or window.groundTone = false.
 * On the average grass pixel (68, 138, 50): full damp is a cool moss (42, 111, 58), full sun straw (116, 152, 33).
 */
export const GROUND_TONE = {
    enabled: true,
    /** Multiplier and 0..255 lift at full damp. */
    damp: [.62, .80, .92] as [number, number, number],
    dampAdd: [0, 0, 12] as [number, number, number],
    /** Multiplier and 0..255 lift at full sun. */
    sunMul: [1, .97, .66] as [number, number, number],
    sunAdd: [48, 18, 0] as [number, number, number],
}

export const groundToneConfig = (): typeof GROUND_TONE => {
    if (typeof window === "undefined") return GROUND_TONE
    const w = window as any
    if (w.groundTone === undefined) w.groundTone = GROUND_TONE
    const live = w.groundTone
    if (live === false) return { ...GROUND_TONE, enabled: false }
    return live && typeof live === "object" ? live : GROUND_TONE
}

export interface GroundToneSource {
    getGroundTone?(): GroundTone | undefined
}

/** One texel per cell, margin included: the multiplier (255 = unchanged) and the lift, RGBA. */
export interface ToneTexels {
    cols: number
    rows: number
    margin: number
    mul: Uint8ClampedArray
    add: Uint8ClampedArray
}

const configKey = (config: typeof GROUND_TONE) => JSON.stringify([config.damp, config.dampAdd, config.sunMul, config.sunAdd])
const texelCache = new WeakMap<GroundTone, { key: string; texels: ToneTexels }>()

export const toneTexels = (tone: GroundTone, config = groundToneConfig()): ToneTexels => {
    const key = configKey(config), cached = texelCache.get(tone)
    if (cached?.key === key) return cached.texels
    const rows = tone.rows.length, cols = (tone.rows[0]?.length ?? 0) / 2
    const mul = new Uint8ClampedArray(cols * rows * 4), add = new Uint8ClampedArray(cols * rows * 4)
    for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
        const { damp, sun } = groundToneCell(tone, x - tone.margin, y - tone.margin)
        const i = (y * cols + x) * 4
        for (let ch = 0; ch < 3; ch++) {
            mul[i + ch] = Math.round(255 * (1 + (config.damp[ch] - 1) * damp) * (1 + (config.sunMul[ch] - 1) * sun))
            add[i + ch] = Math.round(config.sunAdd[ch] * sun + (config.dampAdd?.[ch] ?? 0) * damp)
        }
        mul[i + 3] = add[i + 3] = 255
    }
    const texels = { cols, rows, margin: tone.margin, mul, add }
    texelCache.set(tone, { key, texels })
    return texels
}

/**
 * Tone RGBA ground pixels read from the atlas at world (px, py), exactly as the two sprites tone the screen: the
 * texels sampled bilinearly between cell centres, clamped at the edge, multiplied then lifted.
 */
export const toneGroundPixels = (source: GroundToneSource, pixels: Uint8ClampedArray, px: number, py: number, w: number, h: number): void => {
    const tone = source.getGroundTone?.()
    const config = groundToneConfig()
    if (!tone || !config.enabled) return
    const { cols, rows, margin, mul, add } = toneTexels(tone, config)
    const texel = (v: number, n: number) => {
        const f = (v + .5) / TileSize + margin - .5, i = Math.floor(f)
        return [Math.max(0, Math.min(n - 1, i)), Math.max(0, Math.min(n - 1, i + 1)), f - i] as const
    }
    const xs = Array.from({ length: w }, (_, x) => texel(px + x, cols))
    for (let y = 0; y < h; y++) {
        const [y0, y1, fy] = texel(py + y, rows)
        for (let x = 0; x < w; x++) {
            const o = (y * w + x) * 4
            if (!pixels[o + 3]) continue
            const [x0, x1, fx] = xs[x]
            const a = (y0 * cols + x0) * 4, b = (y0 * cols + x1) * 4, c = (y1 * cols + x0) * 4, d = (y1 * cols + x1) * 4
            for (let ch = 0; ch < 3; ch++) {
                const lerp = (t: Uint8ClampedArray) => (t[a + ch] * (1 - fx) + t[b + ch] * fx) * (1 - fy) + (t[c + ch] * (1 - fx) + t[d + ch] * fx) * fy
                pixels[o + ch] = pixels[o + ch] * lerp(mul) / 255 + lerp(add)
            }
        }
    }
}

/** Tone a grass effect's palette at its ground contact, using the same filtering as map1. */
export const toneGroundColors = (source: GroundToneSource, colors: readonly number[], px: number, py: number): number[] => {
    const tone = source.getGroundTone?.(), config = groundToneConfig()
    if (!tone || !config.enabled) return [...colors]
    const { cols, rows, margin, mul, add } = toneTexels(tone, config)
    const coord = (v: number, n: number) => {
        const f = (v + .5) / TileSize + margin - .5, i = Math.floor(f)
        return [Math.max(0, Math.min(n - 1, i)), Math.max(0, Math.min(n - 1, i + 1)), f - i] as const
    }
    const [x0, x1, fx] = coord(px, cols), [y0, y1, fy] = coord(py, rows)
    const a = (y0 * cols + x0) * 4, b = (y0 * cols + x1) * 4, c = (y1 * cols + x0) * 4, d = (y1 * cols + x1) * 4
    const sample = (t: Uint8ClampedArray, ch: number) =>
        (t[a + ch] * (1 - fx) + t[b + ch] * fx) * (1 - fy) + (t[c + ch] * (1 - fx) + t[d + ch] * fx) * fy
    const multipliers = [0, 1, 2].map(ch => sample(mul, ch) / 255)
    const lifts = [0, 1, 2].map(ch => sample(add, ch))
    // Match ImageData's clamping and rounding, including a lift that exceeds 255.
    const rgb = new Uint8ClampedArray(3)
    return colors.map(color => {
        for (let ch = 0; ch < 3; ch++) rgb[ch] = ((color >> (16 - ch * 8)) & 255) * multipliers[ch] + lifts[ch]
        return (rgb[0] << 16) | (rgb[1] << 8) | rgb[2]
    })
}

/** toneGroundPixels on what a 2D context holds so far (readers that draw map1, then lay map2 and map3 over it). */
export const toneGroundContext = (source: GroundToneSource, context: CanvasRenderingContext2D, px: number, py: number, w: number, h: number): void => {
    if (!source.getGroundTone?.() || !groundToneConfig().enabled) return
    const image = context.getImageData(0, 0, w, h)
    toneGroundPixels(source, image.data, px, py, w, h)
    context.putImageData(image, 0, 0)
}

const texelTexture = (bytes: Uint8ClampedArray, texels: ToneTexels, width: number, height: number): Texture => {
    const canvas = document.createElement("canvas")
    canvas.width = texels.cols
    canvas.height = texels.rows
    const context = canvas.getContext("2d")!
    const image = context.createImageData(texels.cols, texels.rows)
    image.data.set(bytes)
    context.putImageData(image, 0, 0)
    const base = BaseTexture.from(canvas, { scaleMode: SCALE_MODES.LINEAR })
    // Framed to the room: the margin texels stay in the base texture, so filtering at the edge still reads them.
    return new Texture(base, new Rectangle(texels.margin, texels.margin, width, height))
}

/** The two sprites over map1. sync() after every full map render; it rebuilds only when the tone or config changed. */
export class GroundToneLayer extends Container {
    private shade = new Sprite(Texture.EMPTY)
    private warmth = new Sprite(Texture.EMPTY)
    private key?: string
    private source?: GroundToneSource & { width(): number; height(): number }

    constructor() {
        super()
        this.shade.blendMode = BLEND_MODES.MULTIPLY
        this.warmth.blendMode = BLEND_MODES.ADD
        this.addChild(this.shade, this.warmth)
        this.visible = false
    }

    sync = (source: (GroundToneSource & { width(): number; height(): number }) | undefined): void => {
        this.source = source
        const tone = source?.getGroundTone?.(), config = groundToneConfig()
        if (!tone || !config.enabled || typeof document === "undefined") {
            this.visible = false
            this.key = undefined
            return
        }
        const key = `${tone.rows.join("")}|${configKey(config)}|${source.width()}x${source.height()}`
        if (key === this.key) { this.visible = true; return }
        this.key = key
        const texels = toneTexels(tone, config)
        for (const [sprite, bytes] of [[this.shade, texels.mul], [this.warmth, texels.add]] as const) {
            const old = sprite.texture
            sprite.texture = texelTexture(bytes, texels, source.width(), source.height())
            sprite.scale.set(TileSize)
            if (old !== Texture.EMPTY) old.destroy(true)
        }
        this.visible = true
    }

    /** Follows live tuning of window.groundTone. */
    tick = (): void => {
        const config = groundToneConfig()
        const key = config.enabled ? configKey(config) : "off"
        if (this.key && !this.key.endsWith(`|${key}|${this.source?.width()}x${this.source?.height()}`)) this.sync(this.source)
        else if (!this.key && config.enabled && this.source?.getGroundTone?.()) this.sync(this.source)
    }

    destroy(options?: IDestroyOptions | boolean): void {
        for (const sprite of [this.shade, this.warmth]) if (sprite.texture !== Texture.EMPTY) sprite.texture.destroy(true)
        super.destroy(options)
    }
}
